import { useState, useEffect } from 'react';
import { useDocumentCreateText, useDocumentCreateCredential } from '../hooks';
import { useDocumentsStore } from '../store';
import {
  DocumentCategory,
  DocumentType,
  CredentialType,
  CredentialField,
} from '../types';
import {
  DOCUMENT_CATEGORIES,
  CREDENTIAL_FIELD_TEMPLATES,
  CREDENTIAL_TYPE_LABELS,
} from '../constants';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { X, Plus, Trash2 } from 'lucide-react';

interface GedCreateModalProps {
  context?: 'domain' | 'folder' | 'employee';
  contextUuid?: string;
}

export const GedCreateModal: React.FC<GedCreateModalProps> = ({
  context,
  contextUuid,
}) => {
  const [documentType, setDocumentType] = useState<DocumentType | ''>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('autre');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Pour documents texte
  const [textContent, setTextContent] = useState('');

  // Pour identifiants
  const [credentialType, setCredentialType] =
    useState<CredentialType>('urssaf');
  const [credentialFields, setCredentialFields] = useState<CredentialField[]>(
    []
  );

  const { closeCreateModal } = useDocumentsStore();
  const { mutate: createText, isPending: isCreatingText } =
    useDocumentCreateText();
  const { mutate: createCredential, isPending: isCreatingCred } =
    useDocumentCreateCredential();

  const isPending = isCreatingText || isCreatingCred;

  // Auto-suggérer la catégorie selon le type de document
  useEffect(() => {
    if (documentType === 'text') {
      setCategory('document-texte');
    } else if (documentType === 'credentials') {
      setCategory('identifiant');
    }
  }, [documentType]);

  // Auto-remplir les champs selon le type d'identifiant
  useEffect(() => {
    if (documentType === 'credentials' && credentialType) {
      const template = CREDENTIAL_FIELD_TEMPLATES[credentialType];
      if (template) {
        // Créer une copie profonde du template pour éviter les mutations
        setCredentialFields(template.map(field => ({ ...field })));
      }
    }
  }, [credentialType, documentType]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddCredentialField = () => {
    setCredentialFields([
      ...credentialFields,
      { label: '', value: '', type: 'text', sensitive: false },
    ]);
  };

  const handleRemoveCredentialField = (index: number) => {
    setCredentialFields(credentialFields.filter((_, i) => i !== index));
  };

  const handleUpdateCredentialField = (
    index: number,
    updates: Partial<CredentialField>
  ) => {
    setCredentialFields(
      credentialFields.map((field, i) =>
        i === index ? { ...field, ...updates } : field
      )
    );
  };

  const handleSubmit = () => {
    if (!name.trim() || !context || !contextUuid) return;

    if (documentType === 'text') {
      createText(
        {
          name,
          description: description || undefined,
          category,
          tags,
          context,
          contextUuid,
          textContent,
        },
        {
          onSuccess: () => {
            closeCreateModal();
            // Reset form
            setDocumentType('');
            setName('');
            setDescription('');
            setTextContent('');
            setTags([]);
          },
        }
      );
    } else if (documentType === 'credentials') {
      createCredential(
        {
          name,
          description: description || undefined,
          category,
          tags,
          context,
          contextUuid,
          credentialType,
          credentialFields: credentialFields.filter(f => f.label && f.value),
        },
        {
          onSuccess: () => {
            closeCreateModal();
            // Reset form
            setDocumentType('');
            setName('');
            setDescription('');
            setCredentialFields([]);
            setTags([]);
          },
        }
      );
    }
  };

  const isValid =
    name.trim() &&
    context &&
    contextUuid &&
    (documentType === 'text' ? textContent.trim() : true) &&
    (documentType === 'credentials'
      ? credentialFields.filter(f => f.label && f.value).length > 0
      : true);

  return (
    <Modal
      isOpen={true}
      onClose={closeCreateModal}
      title="Créer un document"
      size="lg"
    >
      <div className="space-y-6">
        {/* Type selection */}
        {!documentType && (
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-900">
              Type de document
            </label>
            <div className="grid gap-3 sm:grid-cols-3">
              <button
                onClick={() => setDocumentType('text')}
                className="group hover:border-primary-500 hover:bg-primary-50 rounded-xl border-2 border-neutral-200 bg-white p-6 transition-all"
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-3xl transition-colors group-hover:bg-blue-200">
                  📝
                </div>
                <h3 className="font-semibold text-neutral-900">
                  Document Texte
                </h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Notes, procédures
                </p>
              </button>

              <button
                onClick={() => setDocumentType('credentials')}
                className="group hover:border-primary-500 hover:bg-primary-50 rounded-xl border-2 border-neutral-200 bg-white p-6 transition-all"
              >
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100 text-3xl transition-colors group-hover:bg-purple-200">
                  🔐
                </div>
                <h3 className="font-semibold text-neutral-900">Identifiants</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Comptes, licences
                </p>
              </button>

              <div className="rounded-xl border-2 border-dashed border-neutral-200 bg-neutral-50 p-6 opacity-50">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100 text-3xl">
                  📎
                </div>
                <h3 className="font-semibold text-neutral-900">Fichier</h3>
                <p className="mt-1 text-sm text-neutral-600">
                  Bientôt disponible
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form content */}
        {documentType && (
          <div className="space-y-4">
            {documentType === 'credentials' && (
              <div>
                <label className="mb-2 block text-sm font-medium text-neutral-900">
                  Type d'identifiant
                </label>
                <select
                  value={credentialType}
                  onChange={e =>
                    setCredentialType(e.target.value as CredentialType)
                  }
                  className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                >
                  {Object.entries(CREDENTIAL_TYPE_LABELS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </select>
                <p className="mt-1 text-xs text-neutral-500">
                  Les champs se rempliront automatiquement selon le type choisi
                </p>
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                Nom
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Nom du document"
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Description (optionnelle)"
                rows={2}
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                Catégorie
              </label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value as DocumentCategory)}
                className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
              >
                {DOCUMENT_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Text content */}
            {documentType === 'text' && (
              <div>
                <label className="mb-2 block text-sm font-medium text-neutral-900">
                  Contenu
                </label>
                <textarea
                  value={textContent}
                  onChange={e => setTextContent(e.target.value)}
                  placeholder="Entrez le contenu de votre document..."
                  rows={10}
                  className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 font-mono text-sm transition-colors focus:ring-2"
                />
              </div>
            )}

            {/* Credential fields */}
            {documentType === 'credentials' && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-neutral-900">
                    Champs d'identification
                  </label>
                  <button
                    type="button"
                    onClick={handleAddCredentialField}
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-1 text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter
                  </button>
                </div>
                <div className="space-y-2">
                  {credentialFields.map((field, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={field.label}
                        onChange={e =>
                          handleUpdateCredentialField(index, {
                            label: e.target.value,
                          })
                        }
                        placeholder="Label"
                        className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={e =>
                          handleUpdateCredentialField(index, {
                            value: e.target.value,
                          })
                        }
                        placeholder="Valeur"
                        className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                      />
                      <select
                        value={field.type}
                        onChange={e =>
                          handleUpdateCredentialField(index, {
                            type: e.target.value as CredentialField['type'],
                          })
                        }
                        className="w-32 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                      >
                        <option value="text">Texte</option>
                        <option value="password">Mot de passe</option>
                        <option value="email">Email</option>
                        <option value="url">URL</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => handleRemoveCredentialField(index)}
                        className="text-error-600 hover:bg-error-50 rounded-lg p-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {credentialFields.length === 0 && (
                    <p className="text-sm text-neutral-500">
                      Ajoutez au moins un champ (ex: Nom d'utilisateur, Mot de
                      passe)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-900">
                Tags
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddTag()}
                  placeholder="Ajouter un tag"
                  className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                />
                <Button
                  type="button"
                  onClick={handleAddTag}
                  variant="secondary"
                >
                  Ajouter
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-primary-100 text-primary-700 flex items-center gap-1 rounded px-2 py-1 text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-primary-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={closeCreateModal}
            variant="secondary"
            disabled={isPending}
          >
            Annuler
          </Button>
          {documentType && (
            <Button
              onClick={handleSubmit}
              variant="primary"
              disabled={!isValid || isPending}
            >
              {isPending ? 'Création...' : 'Créer'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
